import type { IO } from 'fp-ts/lib/IO';

import type { KeyOfType } from '../types/KeyOfType';

/**
 * Shortcut bindMethod(obj, 'fn') for obj.fn.bind(obj)
 */

export function bindMethod<
  T,
  K extends KeyOfType<T, (...args: never[]) => unknown>,
>(obj: T, fn: K): OmitThisParameter<T[K]>;

export function bindMethod<
  T,
  K extends KeyOfType<T, (...args: never[]) => unknown>,
>(fn: K): (obj: T) => OmitThisParameter<T[K]>;

export function bindMethod<
  T,
  K extends KeyOfType<T, (...args: never[]) => unknown>,
>(
  obj: T | K,
  fn?: K,
): ((o: T) => OmitThisParameter<T[K]>) | OmitThisParameter<T[K]> {
  if (fn === undefined) {
    return (o: T) => bindMethod(o, obj as K);
  }
  // typescript is not able to check the types although they are correct
  return ((obj as T)[fn] as any).bind(obj);
}

export const callMethod =
  <
    T extends { [k in K]: (...args: TArgs) => unknown },
    K extends keyof any,
    TArgs extends unknown[] = [],
  >(
    fn: K,
    ...args: TArgs
  ) =>
  (obj: T): ReturnType<T[K]> =>
    (obj as any)[fn](...args);

/**
 * Just execute the passed callback.
 *
 * Useful to apply on functions in monads, e.g. callbacks.forEach(exec),
 * or for better readable immediately invoked callbacks: exec(() => ...) vs. (() => ...)()
 */
export function exec<T = void>(callback: IO<T>): T {
  return callback();
}

/**
 * Execute function (e.g. passed from pipe) with given arguments
 *
 * @example
 * pipe(
 *   (a: number, b: number) => a + b,
 *   execWithArgs(10, 32),
 * ); // 42
 */
export const execWithArgs =
  <TArgs extends unknown[]>(...args: TArgs) =>
  <TReturn>(fn: (...a: TArgs) => TReturn): TReturn =>
    fn(...args);

/**
 * Apply a function to a raw value in pipe, then return the original pipe value.
 * This is analogous to various monad's `chainFirst`, but can be called
 * on any (also primitive) value.
 */
export function branch<T>(callback: (t: T) => void): (t: T) => T {
  return (t) => {
    callback(t);
    return t;
  };
}

/**
 * Wrap function execution with another function that may perform
 * something before and after the wrapped function execution
 *
 * Example:
 * ```
 * const createUnicorn = (color: string) => {
 *   console.log(`I'm a ${color} unicorn!`);
 *   return {type: 'unicorn', color};
 * };
 * const makeVerbose = (description: string) => <T>(cb: () => T): T => {
 *   console.log(`${description} started`);
 *   const result = cb();
 *   console.log(`${description} finished`);
 *   return result;
 * };
 *
 * const createVerboseUnicorn = wrap(
 *   makeVerbose('Production of unicorn'),
 *   produceUnicorns
 * );
 * const u = createVerboseUnicorn('rainbow');
 * // Production of unicorn started
 * // I'm a rainbow unicorn!
 * // Production of unicorn finished
 * console.log(u);
 * // {type: 'unicorn', color: 'rainbow'}
 * ```
 */
export const wrap =
  <TReturn>(wrapper: (cb: () => TReturn) => TReturn) =>
  <T extends (...args: any[]) => TReturn>(fn: T): T =>
    ((...args: Parameters<T>) => wrapper(() => fn(...args))) as T;
